"use client"

import React, {useEffect, useRef} from 'react';
import {cn} from "@/lib/utils";
import gsap from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {PortableTextBlock} from "sanity";

gsap.registerPlugin(ScrollTrigger, SplitText);

export default function AnimatedText({
  className = '',
  text,
}: {
  className?: string;
  text: PortableTextBlock[];
}){
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const textEl = containerRef.current;
    if (!textEl) return;

    const paragraphs = [...textEl.querySelectorAll("p")];
    if (paragraphs.length < 1) return;

    const splitInstances: SplitText[] = [];
    const allTargets: (HTMLElement | HTMLImageElement)[] = [];

    paragraphs.forEach((paragraph) => {
      const split = new SplitText(paragraph, {
        type: "words",
        lines: false,
        wordsClass: "word-wrapper",
      });

      splitInstances.push(split);

      // Walk real childNodes to preserve visual order
      paragraph.childNodes.forEach((node) => {
        if (
          node instanceof HTMLElement &&
          node.classList.contains("word-wrapper")
        ) {
          allTargets.push(node);
        }

        if (node instanceof HTMLImageElement) {
          allTargets.push(node);
        }
      });
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: textEl,
        start: "top 50%",
        end: "bottom 60%",
        scrub: 2,
      },
    });

    allTargets.forEach((target, i) => {
      const isImage = target instanceof HTMLImageElement;
      tl.fromTo(
        target,
        { opacity: isImage ? 0.4 : 0.2 },
        {
          opacity: 1,
          ease: "power2.out",
          duration: 0.6,
        },
        i * 0.4
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      splitInstances.forEach((split) => split.revert());
    };
  }, []);

  if (!text) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        "richtext",
        className
      )}
    >
      <PortableText
        value={text}
        components={richTextComponent}
      />
    </div>
  )
}